import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/core/components/auth/store/auth";
import {
  DEFAULT_PROFILE,
  resolveHomeProfile,
  type HomeProfile,
} from "@/action-center/constants/home-profile";
import { getHomeProfile } from "@/action-center/services/home-profile-service";

const HOME_PROFILE_SESSION_KEY = "woba_home_profile";

function readCachedProfile(): string | null {
  try {
    return sessionStorage.getItem(HOME_PROFILE_SESSION_KEY);
  } catch {
    return null;
  }
}

function writeCachedProfile(profile: HomeProfile): void {
  try {
    sessionStorage.setItem(HOME_PROFILE_SESSION_KEY, profile);
  } catch {
    // storage indisponível (ex.: Safari private mode) — segue sem cache
  }
}

export type UseHomeProfileResult = {
  /** Sempre um valor válido do enum (fallback: explorer). */
  profile: HomeProfile;
  confidence: number;
  /** Top 5 intents por volume (90d) — vazio enquanto não há fetch bem-sucedido. */
  topIntents: string[];
  /** Interações com o agente em 90 dias — 0 enquanto não há fetch bem-sucedido. */
  interactions90d: number;
  /** false apenas no primeiro fetch sem cache de sessão. */
  isReady: boolean;
};

/**
 * Resolve o perfil comportamental da empresa (`GET /company/agent-profile`)
 * com fallback resiliente: erro de rede, timeout ou perfil desconhecido
 * nunca quebram o Centro de ações — caem em `explorer`, que mantém a ordem
 * estática atual das pills de sugestão.
 *
 * O último perfil resolvido fica em sessionStorage para evitar reordenação
 * "piscando" ao navegar de volta ao welcome na mesma sessão.
 */
export function useHomeProfile(): UseHomeProfileResult {
  const { userInfo } = useAuth();
  const companyId = userInfo?.user?.leagues_id?.[0] as number | undefined;

  const cachedProfile = readCachedProfile();

  const { data, isSuccess, isError, isLoading } = useQuery({
    queryKey: ["home-profile", companyId],
    queryFn: getHomeProfile,
    enabled: Boolean(companyId),
    staleTime: 30 * 60_000,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  const resolvedProfile = isSuccess
    ? resolveHomeProfile(data.profile)
    : isError
      ? DEFAULT_PROFILE
      : cachedProfile
        ? resolveHomeProfile(cachedProfile)
        : DEFAULT_PROFILE;

  useEffect(() => {
    if (isSuccess) {
      writeCachedProfile(resolveHomeProfile(data.profile));
    }
  }, [isSuccess, data]);

  // Sem empresa ⇒ explorer sem fetch; com cache de sessão ⇒ pronto no 1º paint.
  const isReady =
    !companyId || isSuccess || isError || Boolean(cachedProfile) || !isLoading;

  return {
    profile: resolvedProfile,
    confidence: isSuccess ? (data.confidence ?? 0) : 0,
    topIntents: isSuccess ? (data.top_intents ?? []) : [],
    interactions90d: isSuccess ? (data.interactions_90d ?? 0) : 0,
    isReady,
  };
}
